import React from "react";
import {
  ListItem,
  ListItemButton,
  ListItemText,
  Popper,
  Fade,
  Typography,
  Box,
  ClickAwayListener
} from "@mui/material";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTheme } from '@/context'
import { UpArrow, DownArrow, RightArrow } from "@/assets/images/SidebarSvgUtils";
import { useGlobalStateContext } from "@/context/GlobalContext";

interface AccordionProps {
  title: string;
  id: number;
  menuSelected: number | null;
  setMenuSelected: (id: number | null) => void;
  icon: string;
  sidebarOpen: boolean;
  redirectUrl?: string;
  /** When provided, header click should mimic clicking this submenu item. */
  defaultSubmenuId?: number | null;
  defaultSubmenuName?: string;
  /** Passed through from Sidebar; same handler submenu items use. */
  handleSubmenuSelect?: (index: number, name: string) => void;
  children?: React.ReactNode;
  submenu?: { id: number; key: string; name: string; submenuPath: string }[];
  menuExpanded: number | null;
  setMenuExpanded: (id: number | null) => void;
  setSubmenuSelected: (id: number | null) => void;
  showPopper: HTMLElement | null;
  setShowPopper: (element: HTMLElement | null) => void;
  notificationCount?: number;
  menuColor: string;
  handleMenuSelect: (id: number) => void;
  disabled: boolean;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  redirectUrl,
  children,
  submenu,
  id,
  defaultSubmenuId,
  defaultSubmenuName,
  handleSubmenuSelect,
  menuSelected,
  setMenuSelected,
  menuExpanded,
  setMenuExpanded,
  setSubmenuSelected,
  icon,
  sidebarOpen,
  showPopper,
  setShowPopper,
  notificationCount,
  menuColor,
  disabled
}) => {
  const { darkMode } = useTheme();
  const pathname = usePathname();
  const images = darkMode ? require.context(
    "@/assets/images/ideation-phase-images/dark-theme",
    true
  )
    : require.context(
      "@/assets/images/ideation-phase-images",
      true
    );
  let imagePath = images(`./${icon}`);
  let path;
  if (redirectUrl && redirectUrl !== "/") {
    path = redirectUrl;
  } else {
    const submenuName = submenu && submenu.length ? submenu[0]?.name ?? "" : "";
    path = `/submenu-fallback?name=${encodeURIComponent(submenuName)}&menuTitle=${encodeURIComponent(title)}&menuIcon=${encodeURIComponent(icon)}`;
  }

  const { setShowProductInfo, setShowCreateProduct,setShouldFetchShoppingList } = useGlobalStateContext();
  const toggleSelectMenu = (title: string) => {
    if (title == "Create Product") {
      setShowProductInfo(false);
      setShowCreateProduct(false);
      setShouldFetchShoppingList(true)
    }
    setSubmenuSelected(Number(id + ".1"));
    setMenuSelected(id);
    setMenuExpanded(null);
  };

  const toggleExpandMenu = (

    event: React.MouseEvent<SVGElement | HTMLDivElement, MouseEvent>
  ) => {

    if (id === menuExpanded) {
      setMenuExpanded(null);
      setShowPopper(null); // Close the Popper when menu is deselected
    } else {
      setMenuExpanded(id);
      // Typecast the SVGElement to HTMLElement if necessary
      setShowPopper(event.currentTarget as unknown as HTMLElement); // Set the anchor element for Popper
    }
  };

  const handleClickAway = () => {
    setShowPopper(null); // Close the Popper when clicking outside
  };

  let arrowIcon;

  if (id === menuExpanded) {
    arrowIcon = UpArrow(darkMode ? 'dark' : 'light', toggleExpandMenu);
  } else {
    arrowIcon = DownArrow(darkMode ? 'dark' : 'light', toggleExpandMenu);
  }

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    // Make header click behave like clicking the first submenu item (same state changes).
    if (typeof handleSubmenuSelect === "function" && defaultSubmenuId != null) {
      handleSubmenuSelect(Number(defaultSubmenuId), String(defaultSubmenuName ?? ""));
    } else {
      toggleSelectMenu(title);
    }
    const targetPath = path.split("?")[0];
    if (targetPath === pathname) {
      event.preventDefault();
    }
  };

  return (
    <div>
      <ListItem
        disablePadding
        className={`h-[3.25rem] border-l-4 ${id === menuSelected
          ? "bg-white dark:bg-TJ-Dark"
          : "bg-transparent border-l-transparent"
          }`}
        style={id === menuSelected ? { borderLeftColor: menuColor } : {}}
      >
        <ListItemButton sx={{
          paddingTop: ".75rem",
          paddingBottom: ".75rem",
          paddingLeft: "1.25rem",
          paddingRight: "1.5rem"

        }} disabled={disabled}>
          <Link
            href={path}
            className="flex items-center cursor-pointer w-full"
            onClick={handleLinkClick}
          >
            <Box className="p-1" >
              <Image
                className="w-[24px] h-[24px]"
                src={imagePath.default.src}
                width={500}
                height={500}
                alt="sidebar-icon"

              />
            </Box>
            {sidebarOpen && (
              <div className="relative inline-flex">
                <ListItemText sx={{ paddingLeft: "1rem" }}>
                  <Typography
                    className={` text-[#2E2E2E] dark:text-TJ-white`}
                    sx={{
                      fontFamily: 'Cerebri Sans',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '20px',
                      textAlign: 'left',
                    }}>
                    {title}
                  </Typography>
                </ListItemText>
                {(typeof notificationCount === 'number' && notificationCount >= 0) && (
                  <span className="absolute rounded-full text-[10px] font-medium content-[''] leading-none flex items-center justify-center top-[20%] right-[-2%] translate-x-full -translate-y-2  bg-TJ-Red text-white min-w-[20px] min-h-[20px]">
                    {notificationCount}
                  </span>
                )}
              </div>
            )}
          </Link>
          {children && title !== "My Notes" &&    //REMOVE THIS CONDITION WHEN MY NOTES ALSO NEEDS SUBMENU
            (sidebarOpen ? (
              <Box
                sx={{ paddingLeft: ".5rem" }}
                className="flex cursor-pointer justify-center p-[1px]">
                {arrowIcon}
              </Box>
            ) : (
              <Box id={`sideBarArrow${id}`} className="flex cursor-pointer justify-center">
                {RightArrow(darkMode ? 'dark' : 'light', toggleExpandMenu)}
              </Box>
            ))}
        </ListItemButton>
      </ListItem>
      {id === menuExpanded &&
        title !== "My Notes" &&
        (sidebarOpen ? (
          <div>{children}</div>
        ) : showPopper && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Popper
              sx={{ zIndex: 1200 }}
              open={Boolean(showPopper)}
              anchorEl={showPopper}
              placement="right-start"
              transition
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [-17, 16],
                  },
                },
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: ["right-end"],
                  },
                },
              ]}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps}>
                  {children && React.isValidElement(children) ? children : <></>}
                </Fade>
              )}
            </Popper>
          </ClickAwayListener>
        ))}
    </div>
  );
};

export default Accordion;
